import React, { useCallback, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Snackbar,
  Alert,
  Grid,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add, Refresh, Search } from '@mui/icons-material';
import { getVirtualAccountStats, createVirtualAccounts, queryWemaTransaction } from '../../services/api';

const STATUS_LABELS = {
  AVAILABLE: 'Available',
  ACTIVE: 'Active',
  CONSUMED: 'Consumed',
  EXPIRED: 'Expired',
  QUARANTINED: 'Quarantined',
  INACTIVE: 'Inactive',
};

/** Map TransQuery raw response to admin-friendly display rows and status. Uses first item in transactions[] when present. */
const mapTransQueryResponse = (raw) => {
  if (!raw || typeof raw !== 'object') return { status: 'Unknown', rows: [] };
  const r = Array.isArray(raw.transactions) && raw.transactions.length > 0 ? raw.transactions[0] : raw;
  const nibss = (r.nibssresponse || r.nibssResponse || '').toString().trim();
  const send = (r.sendresponse || r.sendResponse || '').toString().trim();
  const isSuccess = nibss === '00' && send === '00-Okay';
  const status = isSuccess ? 'Successful' : `Unsuccessful (NIBSS: ${nibss || '—'}, Send: ${send || '—'})`;

  const fieldMap = [
    ['originatoraccountnumber', "Sender's Account Number"],
    ['originatorname', "Sender's Account Name"],
    ['amount', 'Amount'],
    ['bankname', "Sender's Bank Name"],
    ['craccountname', "Receiver's Account Name"],
    ['craccount', "Receiver's Account Number"],
    ['requestdate', 'Transaction Date'],
    ['sessionid', 'Session ID'],
  ];
  const getVal = (key) => {
    const v = r[key];
    if (v !== undefined && v !== null && v !== '') return v;
    return '';
  };
  const rows = fieldMap
    .map(([key, label]) => ({ label, value: getVal(key) ? String(getVal(key)) : '—' }));

  return { status, isSuccess, rows };
};

const VirtualAccounts = () => {
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [createCount, setCreateCount] = useState('');
  const [creating, setCreating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [queryTransOpen, setQueryTransOpen] = useState(false);
  const [querySessionId, setQuerySessionId] = useState('');
  const [queryCraccount, setQueryCraccount] = useState('');
  const [queryAmount, setQueryAmount] = useState('');
  const [queryTxndate, setQueryTxndate] = useState('');
  const [querying, setQuerying] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [queryError, setQueryError] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getVirtualAccountStats();
      setStats({ total: data.total ?? 0, by_status: data.by_status ?? {} });
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.error || e.message || 'Failed to load stats', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleOpenQueryTrans = () => {
    setQueryTransOpen(true);
    setQuerySessionId('');
    setQueryCraccount('');
    setQueryAmount('');
    setQueryTxndate('');
    setQueryResult(null);
    setQueryError(null);
  };

  const handleCloseQueryTrans = () => {
    setQueryTransOpen(false);
    setQueryResult(null);
    setQueryError(null);
  };

  const handleQueryTrans = async () => {
    const sessionid = querySessionId.trim();
    const craccount = queryCraccount.trim();
    if (!sessionid && !craccount) {
      setSnackbar({ open: true, message: 'Enter Session ID or CR Account (at least one)', severity: 'warning' });
      return;
    }
    setQuerying(true);
    setQueryError(null);
    setQueryResult(null);
    try {
      const body = {};
      if (sessionid) body.sessionid = sessionid;
      if (craccount) body.craccount = craccount;
      const amountStr = queryAmount.trim();
      if (amountStr !== '') {
        const num = parseFloat(amountStr);
        if (!Number.isNaN(num)) body.amount = num;
      }
      const txndate = queryTxndate.trim();
      if (txndate) body.txndate = txndate;
      const result = await queryWemaTransaction(body);
      setQueryResult(result);
    } catch (e) {
      const errMsg = e.response?.data?.error || e.response?.data?.detail || e.message || 'Query failed';
      setQueryError(errMsg);
    } finally {
      setQuerying(false);
    }
  };

  const handleCreate = async () => {
    const count = createCount.trim() === '' ? 100 : parseInt(createCount, 10);
    if (isNaN(count) || count < 1 || count > 10000) {
      setSnackbar({ open: true, message: 'Enter a number between 1 and 10000 (or leave empty for 100)', severity: 'warning' });
      return;
    }
    setCreating(true);
    try {
      const result = await createVirtualAccounts({ count, dynamic: true });
      setSnackbar({
        open: true,
        message: `Created ${result.created} virtual account(s). Requested: ${result.requested}.`,
        severity: 'success',
      });
      setCreateCount('');
      loadStats();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.error || e.message || 'Failed to create accounts',
        severity: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Virtual Account Management (Wema)
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Stats by status</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button startIcon={<Search />} onClick={handleOpenQueryTrans} size="small" variant="outlined">
                    Query Trans
                  </Button>
                  <Button startIcon={<Refresh />} onClick={loadStats} disabled={loading} size="small">
                    Refresh
                  </Button>
                </Box>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`Total: ${stats.total}`} color="primary" sx={{ fontWeight: 600 }} />
                  {Object.entries(stats.by_status).map(([status, count]) => (
                    <Chip
                      key={status}
                      label={`${STATUS_LABELS[status] || status}: ${count}`}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Create more VA
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Same flow as the generate_wema_virtual_accounts command. Leave empty to create 100.
              </Typography>
              <TextField
                fullWidth
                type="number"
                label="Number to create"
                placeholder="100"
                value={createCount}
                onChange={(e) => setCreateCount(e.target.value)}
                inputProps={{ min: 1, max: 10000 }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? <CircularProgress size={24} /> : 'Create more VA'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={queryTransOpen} onClose={handleCloseQueryTrans} maxWidth="sm" fullWidth>
        <DialogTitle>Query transaction (TransQuery)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter at least one: Session ID or CR Account. Uses Wema NIP TransQuery for dispute resolution.
          </Typography>
          <TextField
            fullWidth
            label="Session ID"
            value={querySessionId}
            onChange={(e) => setQuerySessionId(e.target.value)}
            placeholder="e.g. from notification"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="CR Account"
            value={queryCraccount}
            onChange={(e) => setQueryCraccount(e.target.value)}
            placeholder="Credit account number (e.g. 6941665696)"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={queryAmount}
            onChange={(e) => setQueryAmount(e.target.value)}
            placeholder="e.g. 201.00"
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="date"
            label="Transaction date (optional)"
            value={queryTxndate}
            onChange={(e) => setQueryTxndate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().slice(0, 10) }}
            helperText="Sent as YYYY-MM-DD (e.g. 2020-01-01)"
            sx={{ mb: 2 }}
          />
          {queryError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {queryError}
            </Alert>
          )}
          {queryResult && (() => {
            const { status, isSuccess, rows } = mapTransQueryResponse(queryResult);
            return (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Transaction details</Typography>
                <Alert severity={isSuccess ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  Status: {status}
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {rows.map(({ label, value }) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-all' }}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>Raw response</Typography>
                <Box
                  component="pre"
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 200,
                    fontSize: '0.75rem',
                  }}
                >
                  {JSON.stringify(queryResult, null, 2)}
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQueryTrans}>Close</Button>
          <Button variant="contained" onClick={handleQueryTrans} disabled={querying}>
            {querying ? <CircularProgress size={24} /> : 'Query'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VirtualAccounts;
